<template>
  <BaseSheet :isOpen="isOpen" size="sm" @close="$emit('close')">
    <slot />

    <!-- Actions -->
    <div class="flex items-center gap-3" :class="actionsClass">
      <button type="button" @click="$emit('close')"
        class="flex-1 h-[48px] rounded-[14px] text-[14px] font-semibold bd-cancel active:scale-[0.97] transition-all duration-150">
        {{ closeText }}
      </button>
      <button :type="submitType" :form="formId" @click="$emit('confirm')" :disabled="sending"
        class="flex-1 h-[48px] rounded-[14px] text-[14px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.97] transition-all duration-150 disabled:opacity-70 disabled:active:scale-100"
        :class="ctaClass">
        <svg v-if="sending" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
          fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span v-if="!sending">{{ confirmText }}</span>
      </button>
    </div>
  </BaseSheet>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import BaseSheet from './BaseSheet.vue';

export type DialogColor = 'primary' | 'danger' | 'warning'

const props = withDefaults(defineProps<{
  isOpen: boolean;
  sending: boolean;
  confirmText: string;
  closeText: string;
  color?: DialogColor;
  actionsClass?: string;
  submitType?: 'button' | 'submit';
  formId?: string;
}>(), {
  isOpen: false,
  sending: false,
  color: 'primary',
  actionsClass: '',
  submitType: 'button',
});

defineEmits<{
  (e: 'close'): void;
  (e: 'confirm'): void;
}>();

const ctaClassMap: Record<DialogColor, string> = {
  primary: 'bd-cta-primary',
  danger: 'bd-cta-danger',
  warning: 'bd-cta-warning',
};

const ctaClass = computed(() => ctaClassMap[props.color]);
</script>

<style scoped>
/* Light mode */
.bd-cancel {
  color: var(--muted-foreground, #64748b);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.bd-cta-primary, .bd-cta-danger, .bd-cta-warning {
  cursor: pointer;
}
.bd-cancel:active {
  background: rgba(0, 0, 0, 0.07);
}
.bd-cta-primary {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}
.bd-cta-danger {
  background: linear-gradient(135deg, #E3211C 0%, #C41E1A 100%);
  box-shadow: 0 6px 24px rgba(227, 33, 28, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.12);
}
.bd-cta-warning {
  background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
  box-shadow: 0 6px 24px rgba(245, 158, 11, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

/* Dark mode */
:is(.dark *).bd-cancel {
  color: var(--muted-foreground, #94a3b8);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.07);
}
:is(.dark *).bd-cancel:active {
  background: rgba(255, 255, 255, 0.10);
}
:is(.dark *).bd-cta-primary {
  box-shadow: 0 4px 20px rgba(6, 200, 255, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}
:is(.dark *).bd-cta-danger {
  box-shadow: 0 4px 20px rgba(227, 33, 28, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.08);
}
:is(.dark *).bd-cta-warning {
  box-shadow: 0 4px 20px rgba(245, 158, 11, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}
</style>
